import {
  AccessRequestClientModel,
  isAccessStatusClosed,
  isAccessStatusOpen,
  isAccessStatusPendingApproval,
  isAccessStatusWaiting,
  useGetMultipleAccessRequests,
} from '@client-app-sdk';
import { Loader } from '@common/ui/Loader';
import { Alert } from '@mui/material';
import AddAccessStates from '@MyAccess/components/AddAccessStates';
import { useCallback, useEffect, useState } from 'react';

const TIMEOUT_WAITING = 20 * 1000; // 20 seconds

interface AddAccessProcessProps {
  requestIds: string[];
  onUseAccess: () => void;
  onCheckDetails: (failedRequestsIds: string[]) => void;
}

export default function AddAccessProcess({ requestIds, onUseAccess, onCheckDetails }: AddAccessProcessProps) {
  const { requests, isFetched } = useGetMultipleAccessRequests(requestIds, 2000);

  if (!isFetched) {
    return <Loader size={72} sx={{ my: 4 }} />;
  }

  if (requests.length === 0 || requests.length !== requestIds.length) {
    return <Alert severity="error">Failed to fetch access requests</Alert>;
  }

  return <AddAccessProcessWatcher {...{ requests, onUseAccess, onCheckDetails }} />;
}

function AddAccessProcessWatcher({
  requests,
  onUseAccess,
  onCheckDetails,
}: {
  requests: AccessRequestClientModel[];
  onUseAccess: () => void;
  onCheckDetails: (failedRequestsIds: string[]) => void;
}) {
  const isStillWaiting = requests.some((request) => isAccessStatusWaiting(request.status.status));
  const allRequestsNeedApprover = requests.every((request) => isAccessStatusPendingApproval(request.status.status));

  const successfulRequests = requests.filter((request) => isAccessStatusOpen(request.status.status));
  const failedRequests = requests.filter((request) => isAccessStatusClosed(request.status.status));
  const hasApprovers = requests.some((request) => isAccessStatusPendingApproval(request.status.status));

  const failReasons = failedRequests
    .map((request) => request.status.description)
    .filter((r) => r)
    .join(', ');

  const handleOnCheckDetails = () => {
    if (onCheckDetails) {
      onCheckDetails(failedRequests.map((r) => r.id));
    }
  };

  if (allRequestsNeedApprover) {
    return (
      <AddAccessStates
        status="success"
        title="Request is sent for approval"
        hasApprovers
        onCheckDetails={handleOnCheckDetails}
      />
    );
  }

  if (isStillWaiting) {
    return <AddAccessProcessLoader requests={requests} onCheckDetails={onCheckDetails} />;
  }

  if (successfulRequests.length > 0 && failedRequests.length === 0) {
    return (
      <AddAccessStates
        status="success"
        title="You are now connected!"
        onUseAccess={onUseAccess}
        hasApprovers={hasApprovers}
      />
    );
  }

  if (successfulRequests.length === 0 && failedRequests.length > 0) {
    return (
      <AddAccessStates
        status="error"
        title="Resources failed to connect"
        subtitle={failReasons}
        onCheckDetails={handleOnCheckDetails}
        dataProps={JSON.stringify({
          integrationType: requests[0]?.access_groups[0].integration.type,
          resourceType: requests[0]?.access_groups[0].resource_types[0].name,
        })}
      />
    );
  }

  return (
    <AddAccessStates
      status="error"
      title={`${failedRequests.length} out of ${requests.length} resources failed to connect`}
      subtitle={failReasons}
      onUseAccess={onUseAccess}
      onCheckDetails={handleOnCheckDetails}
      hasApprovers={hasApprovers}
    />
  );
}

function AddAccessProcessLoader({
  requests,
  onCheckDetails,
}: {
  requests: AccessRequestClientModel[];
  onCheckDetails: (failedRequestsIds: string[]) => void;
}) {
  const [timedOut, setTimedOut] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
  }, [timer]);

  useEffect(() => {
    if (!timer) {
      setTimer(setTimeout(() => setTimedOut(true), TIMEOUT_WAITING));
    }

    return clearTimer;
  }, [clearTimer, timer]);

  if (timedOut) {
    return (
      <AddAccessStates
        status="error"
        title="The request is taking longer than expected"
        onCheckDetails={() => onCheckDetails(requests.map((r) => r.id))}
      />
    );
  }

  return (
    <Loader
      size={72}
      loadingSentences={['Connecting...', 'This may take a few minutes...', 'Please wait...', 'Working on it...']}
      sx={{ my: 4 }}
    />
  );
}
